import ListItem from './Item';
import { Line } from '@/utils/Line';
import isEqual from 'lodash/isEqual';
import kebabCase from 'lodash/kebabCase';
import React, { Fragment, memo, useCallback, useMemo } from 'react';
import { nullable } from 'zod';
export { default as ListItem} from './Item';
export { default as ListEmpty } from './ListEmpty';

const DEFAULTS = {
    light: 'transparent',
    dark: 'transparent',
}

export const ItemList = memo((props) => {

    const {
        items = [],
        seperator = null, 
        renderItem = null,
        children = null,
        gap = 0,
        key = null,
    } = props ?? {};

    const filteredItems = useMemo(() => items.filter(i => i != null && i != false), [items]);
    
    const hasRenderItem = typeof renderItem === 'function';
    
    const localSeperator = typeof seperator === 'function' ? seperator(gap) : seperator;

    const keyExtractor = useCallback((item, index) => {
        const section = item?.section ?? item?.title ?? item?.subtitle ?? item?.pretitle ?? item?.id ?? index;
        return kebabCase(section+index);
    }, []);
    
    const localRenderItem = useCallback((item, index) => {
        const key = keyExtractor(item, index);
        if (localSeperator && index < filteredItems.length - 1 && filteredItems.length != 1) {
            return (
                <>
                    <ListItem {...DEFAULTS} {...item} item={item} key={key} />
                    {localSeperator}
                </>
            );

        }

        return <ListItem {...DEFAULTS} {...item} item={item} key={key} />;
    }, [localSeperator, filteredItems]);

    const externalRenderItem = useCallback((item, index) => {
        
        if (localSeperator && index < filteredItems.length - 1 && filteredItems.length != 1) {
            const key = keyExtractor(item, index);
            return (
                <Fragment key={`key-${key}-${index}`}>
                    {renderItem(item, index)}
                    {localSeperator}
                </Fragment>
            );
        }

        return renderItem(item, index);
    }, [localSeperator, renderItem, filteredItems]);

    if (filteredItems.length === 0 && !children)
        return null;
    

    if (children) {

        if (!Array.isArray(children)) {
            return children;
        }

        const childrenArray = React.Children.toArray(children);
        return childrenArray.map((child) => child == null ? null : child)
            .filter(Boolean)
            .map((child, index) => {

                if (seperator && index < childrenArray.length - 1 && childrenArray.length != 1) {
                    return (
                        <>
                            {child}
                            {seperator}
                        </>
                    );
                }

                return child;
            });

    }

    return filteredItems?.map(hasRenderItem ? externalRenderItem : localRenderItem);

}, isEqual);

export const ItemSeperatorList = memo((props) => {
    
    const {horizontal = false, gap = 15, ...rest} = props ?? {};

    const seperator = useCallback((gap = 15) => {
        const lineProps = horizontal ? { w: 1, h: '100%', mx: gap } : { my: gap, w: '100%', h: 1};
        
        return <Line {...lineProps} />;
    }, [horizontal, gap]);

    return (
        <ItemList gap={gap} seperator={seperator} {...rest} />
    )

}, isEqual);

export default ItemList;